import { Group, GroupActiveStatus } from 'Services/Api/Groups/Types';
import { getCanBePropertyToAncestors } from '../EditGroup/EditGroup.helpers';
import { AddGroupValues } from '../Group.types';

/**
 * Determines the default hierarchy label for a new group based on:
 * - Whether this is a tenant (level 1) → always "Organization"
 * - For groups (level 2+):
 *   - New tenants (is_new=true): no default (user must select)
 *   - Old tenants (is_new=false):
 *     - isProperty=true on parent → default "Property"
 *     - isProperty=false on parent → no default (user must select)
 */
function getDefaultHierarchyLabel(
	isTenant: boolean,
	parentGroup?: Group
): string | undefined {
	// Level 1 (tenant) → always Organization
	if (isTenant) {
		return 'Organization';
	}

	// Level 2+ (group under a tenant)
	if (!parentGroup) {
		return undefined;
	}

	const isNewTenant = parentGroup.is_new;

	// New tenants: level 2+ always has no default
	if (isNewTenant) {
		return undefined;
	}

	// Old tenants (is_new=false or undefined):
	// If parent's isProperty is true → default to "Property"
	// If parent's isProperty is false → no default
	if (parentGroup.isProperty) {
		return 'Property';
	}

	return undefined;
}

export const getInitialValues = (group: Group | undefined) => {
	const isTenant = !group;
	const initialValues = {
		active: GroupActiveStatus.Active,
		isProperty: false,
		moduleEnabled: true,
		canBePropertyToAncestors: true,
		hierarchyLabel: getDefaultHierarchyLabel(isTenant, group)
	} as AddGroupValues;

	if (group) {
		initialValues.canBePropertyToAncestors = getCanBePropertyToAncestors(
			group
		);
		initialValues.parent = {
			id: group.id,
			name: group.name,
			moduleSettings: group.moduleSettings,
			isProperty: group.isProperty
		};
		initialValues.tenant = group.parents.length
			? group.parents[0]
			: initialValues.parent;

		initialValues.moduleEnabled = Boolean(
			initialValues.tenant.moduleSettings?.moduleEnabled
		);
	}

	return initialValues;
};
